import {useCallback, useEffect, useRef, useState} from 'react'

// A scrollLeft of a fractionally-sized track never reaches scrollWidth - clientWidth exactly.
const SCROLL_END_TOLERANCE = 1
const SCROLL_STEP_RATIO = 0.9

const getScrollState = el => ({
  atStart: el.scrollLeft <= 0,
  atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - SCROLL_END_TOLERANCE
})

export default function useCardsCarouselScroll() {
  const scrollRef = useRef(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(true)

  const syncState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const {atStart: start, atEnd: end} = getScrollState(el)

    setAtStart(start)
    setAtEnd(end)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    syncState()

    const resizeObserver = new ResizeObserver(syncState)
    resizeObserver.observe(el)
    el.addEventListener('scroll', syncState, {passive: true})

    return () => {
      resizeObserver.disconnect()
      el.removeEventListener('scroll', syncState)
    }
  }, [syncState])

  const scrollByStep = useCallback(direction => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({left: el.clientWidth * SCROLL_STEP_RATIO * direction, behavior: 'smooth'})
  }, [])

  const scrollPrev = useCallback(() => scrollByStep(-1), [scrollByStep])
  const scrollNext = useCallback(() => scrollByStep(1), [scrollByStep])

  return {atEnd, atStart, scrollNext, scrollPrev, scrollRef}
}
